import React, { useState, useRef } from 'react';
import { Plus, Trash2, Edit2, X, Save, Package, Upload, Image, Star, FileText, Tag, Globe, Calendar, Percent, DollarSign, Clock, Settings, Shield, Truck, RotateCcw, ShoppingBag } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REGIONS = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'INTL', name: 'International' }
];

const PRODUCT_STATUSES = [
  { value: 'active', label: 'Active - Available for Purchase', color: 'green' },
  { value: 'coming_soon', label: 'Coming Soon - Newsletter Signup', color: 'blue' },
  { value: 'pre_order', label: 'Pre-Order - Accept Orders/Deposits', color: 'amber' }
];

const ProductsTab = ({ 
  products, 
  productForm, 
  setProductForm, 
  showProductDialog, 
  setShowProductDialog,
  handleCreateProduct,
  handleDeleteProduct,
  headers,
  fetchData
}) => {
  const [editingProduct, setEditingProduct] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [inlineStockEdit, setInlineStockEdit] = useState(null);
  const [inlineStockValue, setInlineStockValue] = useState('');
  const fileInputRef = useRef(null);
  const multiFileInputRef = useRef(null);
  const createFileInputRef = useRef(null);
  const pdfInputRef = useRef(null);

  // ========== Image Upload ==========
  const handleImageUpload = async (file, isCreate = false) => {
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/api/products/upload-image`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Store as relative URL so it works on any domain
      const imageUrl = response.data.file_url;
      
      if (isCreate) {
        setProductForm({ ...productForm, image_url: imageUrl });
      } else {
        setEditForm({ ...editForm, image_url: imageUrl });
      }
      
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload image');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  // ========== Inline Stock Edit ==========
  const handleInlineStockSave = async (productId) => {
    try {
      const quantity = inlineStockValue === '' ? null : parseInt(inlineStockValue);
      await axios.put(`${API_URL}/api/products/${productId}`, { stock_quantity: quantity }, { headers });
      toast.success('Stock updated!');
      setInlineStockEdit(null);
      if (fetchData) fetchData();
    } catch (error) {
      toast.error('Failed to update stock');
    }
  };

  const handleEditClick = (product) => {
    setEditingProduct(product.id);
    // Normalize images to object format { url, description }
    const normalizedImages = (product.images || []).map(img => 
      typeof img === 'string' 
        ? { url: img, description: '' } 
        : { url: img.url, description: img.description || '' }
    );
    
    setEditForm({
      name: product.name || '',
      description: product.description || '',
      price: product.price || '',
      image_url: product.image_url || '',
      image_description: product.image_description || '',
      images: normalizedImages,
      category: product.category || 'freeze-dryer',
      size: product.size || '',
      features: Array.isArray(product.features) ? product.features : [],
      specifications: product.specifications || {},
      in_stock: product.in_stock !== false,
      stock_quantity: product.stock_quantity ?? '',
      low_stock_threshold: product.low_stock_threshold || 5,
      featured: product.featured || false,
      // Sale fields
      on_sale: product.on_sale || false,
      sale_type: product.sale_type || 'fixed',
      sale_value: product.sale_value || '',
      sale_start_date: product.sale_start_date || '',
      sale_end_date: product.sale_end_date || '',
      // Regional Sale Prices (Charm Pricing)
      regional_sale_prices: product.regional_sale_prices || {},
      // Region-specific pricing
      region_prices: product.region_prices || {},
      region_sale_configs: product.region_sale_configs || {},
      // PDF downloads
      flyer_pdf_url: product.flyer_pdf_url || '',
      spec_sheet_url: product.spec_sheet_url || '',
      // Product status
      status: product.status || 'active', // active, coming_soon, pre_order
      preorder_deposit: product.preorder_deposit || '',
      estimated_availability: product.estimated_availability || '',
      // Badges
      badges: product.badges || {},
      // Warranty
      warranty_info: product.warranty_info || '',
      // Add-ons
      addons: product.addons || [],
      // Ships With (What's Included)
      ships_with: product.ships_with || []
    });
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
    setEditForm({});
  };

  // ========== Features List Management ==========
  const addFeature = () => {
    setEditForm({ ...editForm, features: [...(editForm.features || []), ''] });
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...(editForm.features || [])];
    newFeatures[index] = value;
    setEditForm({ ...editForm, features: newFeatures });
  };

  const removeFeature = (index) => {
    const newFeatures = (editForm.features || []).filter((_, i) => i !== index);
    setEditForm({ ...editForm, features: newFeatures });
  };

  // ========== Ships With (What's Included) Management ==========
  const addShipsWith = () => {
    setEditForm({ ...editForm, ships_with: [...(editForm.ships_with || []), ''] });
  };

  const updateShipsWith = (index, value) => {
    const newShipsWith = [...(editForm.ships_with || [])];
    newShipsWith[index] = value;
    setEditForm({ ...editForm, ships_with: newShipsWith });
  };

  const removeShipsWith = (index) => {
    const newShipsWith = (editForm.ships_with || []).filter((_, i) => i !== index);
    setEditForm({ ...editForm, ships_with: newShipsWith });
  };

  // ========== Multiple Images Management ==========
  const handleMultipleImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const uploadedImages = [...(editForm.images || [])];
    
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API_URL}/api/products/upload-image`, formData, {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        // Store as relative URL so it works on any domain
        const imageUrl = response.data.file_url;
        // Store as object with url and description
        uploadedImages.push({ url: imageUrl, description: '' });
      }
      
      setEditForm({ ...editForm, images: uploadedImages });
      toast.success(`${files.length} image(s) uploaded!`);
    } catch (error) {
      toast.error('Failed to upload some images');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    const newImages = (editForm.images || []).filter((_, i) => i !== index);
    setEditForm({ ...editForm, images: newImages });
  };

  // ========== PDF Upload ==========
  const handlePdfUpload = async (file, field) => {
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed');
      return;
    }
    
    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/api/products/upload-pdf`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Store as relative URL so it works on any domain
      const pdfUrl = response.data.file_url;
      setEditForm({ ...editForm, [field]: pdfUrl });
      toast.success('PDF uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload PDF');
      console.error('Upload error:', error);
    } finally {
      setUploadingPdf(false);
    }
  };

  // ========== Region Price Management ==========
  const updateRegionPrice = (region, value) => {
    const newRegionPrices = { ...(editForm.region_prices || {}) };
    if (value === '' || value === null) {
      delete newRegionPrices[region];
    } else {
      newRegionPrices[region] = parseFloat(value);
    }
    setEditForm({ ...editForm, region_prices: newRegionPrices });
  };

  const updateRegionSaleConfig = (region, field, value) => {
    const newConfigs = { ...(editForm.region_sale_configs || {}) };
    if (!newConfigs[region]) {
      newConfigs[region] = { enabled: false, type: 'percentage', value: 0 };
    }
    newConfigs[region] = { ...newConfigs[region], [field]: value };
    setEditForm({ ...editForm, region_sale_configs: newConfigs });
  };

  // ========== Specifications Management ==========
  const [newSpecKey, setNewSpecKey] = useState('');
  const [newSpecValue, setNewSpecValue] = useState('');
  
  const addSpecification = () => {
    if (!newSpecKey.trim()) return;
    const newSpecs = { ...(editForm.specifications || {}) };
    newSpecs[newSpecKey.trim()] = newSpecValue.trim();
    setEditForm({ ...editForm, specifications: newSpecs });
    setNewSpecKey('');
    setNewSpecValue('');
  };
  
  const removeSpecification = (key) => {
    const newSpecs = { ...(editForm.specifications || {}) };
    delete newSpecs[key];
    setEditForm({ ...editForm, specifications: newSpecs });
  };

  // ========== Add-ons Management ==========
  const [newAddon, setNewAddon] = useState({ name: '', price: '', type: 'checkbox', description: '' });
  
  const addAddon = () => {
    if (!newAddon.name.trim() || !newAddon.price) return;
    const addons = [...(editForm.addons || [])];
    addons.push({
      id: `addon_${Date.now()}`,
      name: newAddon.name.trim(),
      price: parseFloat(newAddon.price),
      price_cad: newAddon.price_cad ? parseFloat(newAddon.price_cad) : null,
      type: newAddon.type,
      description: newAddon.description.trim(),
      allow_quantity: newAddon.type === 'checkbox' // Only allow quantity for checkbox add-ons
    });
    setEditForm({ ...editForm, addons });
    setNewAddon({ name: '', price: '', price_cad: '', type: 'checkbox', description: '' });
  };
  
  const removeAddon = (id) => {
    const addons = (editForm.addons || []).filter(a => a.id !== id);
    setEditForm({ ...editForm, addons });
  };

  // ========== Badge Management ==========
  const updateBadge = (key, value) => {
    const badges = { ...(editForm.badges || {}) };
    badges[key] = value;
    setEditForm({ ...editForm, badges });
  };

  const handleSaveEdit = async (productId) => {
    setSaving(true);
    try {
      // Filter out empty features
      const cleanedFeatures = (editForm.features || []).filter(f => f && f.trim());
      
      const updateData = {
        name: editForm.name,
        description: editForm.description,
        price: parseFloat(editForm.price),
        image_url: editForm.image_url,
        image_description: editForm.image_description || null,
        images: editForm.images || [],
        category: editForm.category,
        size: editForm.size || null,
        features: cleanedFeatures,
        specifications: editForm.specifications || {},
        in_stock: editForm.in_stock,
        stock_quantity: editForm.stock_quantity !== '' ? parseInt(editForm.stock_quantity) : null,
        low_stock_threshold: parseInt(editForm.low_stock_threshold) || 5,
        featured: editForm.featured || false,
        // Sale fields
        on_sale: editForm.on_sale || false,
        sale_type: editForm.sale_type || 'fixed',
        sale_value: editForm.sale_value ? parseFloat(editForm.sale_value) : null,
        sale_start_date: editForm.sale_start_date || null,
        sale_end_date: editForm.sale_end_date || null,
        // Regional Sale Prices (Charm Pricing)
        regional_sale_prices: editForm.regional_sale_prices && Object.keys(editForm.regional_sale_prices).length > 0 
          ? editForm.regional_sale_prices 
          : null,
        // Region-specific pricing
        region_prices: editForm.region_prices || null,
        region_sale_configs: editForm.region_sale_configs || null,
        // PDF downloads
        flyer_pdf_url: editForm.flyer_pdf_url || null,
        spec_sheet_url: editForm.spec_sheet_url || null,
        // Product status
        status: editForm.status || 'active',
        preorder_deposit: editForm.preorder_deposit ? parseFloat(editForm.preorder_deposit) : null,
        estimated_availability: editForm.estimated_availability || null,
        // Badges
        badges: editForm.badges || null,
        // Warranty
        warranty_info: editForm.warranty_info || null,
        // Add-ons
        addons: editForm.addons || null,
        // Ships With (What's Included)
        ships_with: editForm.ships_with?.filter(s => s.trim()) || null
      };

      await axios.put(`${API_URL}/api/products/${productId}`, updateData, { headers });
      toast.success('Product updated successfully!');
      setEditingProduct(null);
      setEditForm({});
      if (fetchData) fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update product');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="animate-fade-in" data-testid="products-tab">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#0a0203]">Products</h1>
          <p className="text-gray-600 mt-1">Manage your product catalogue</p>
        </div>
        <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
          <DialogTrigger asChild>
            <Button className="bg-[#003456] hover:bg-[#001d30]" data-testid="add-product-btn">
              <Plus className="h-5 w-5 mr-2" /> Add Product
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Add New Product</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateProduct} className="space-y-4">
              <Input 
                placeholder="Product Name" 
                value={productForm.name} 
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} 
                required 
                data-testid="product-name-input" 
              />
              <Input 
                placeholder="Description" 
                value={productForm.description} 
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} 
                required 
                data-testid="product-description-input" 
              />
              <Input 
                type="number" 
                step="0.01" 
                placeholder="Price (CAD)" 
                value={productForm.price} 
                onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} 
                required 
                data-testid="product-price-input" 
              />
              <Input 
                placeholder="Image URL" 
                value={productForm.image_url} 
                onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })} 
                required 
                data-testid="product-image-input" 
              />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={productForm.category}
                    onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="freeze-dryer">Freeze Dryer</option>
                    <option value="accessory">Accessory</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Size</label>
                  <Input 
                    placeholder="e.g., Small, Medium, Large" 
                    value={productForm.size || ''} 
                    onChange={(e) => setProductForm({ ...productForm, size: e.target.value })} 
                  />
                </div>
              </div>
              <Input 
                placeholder="Features (comma-separated)" 
                value={productForm.features} 
                onChange={(e) => setProductForm({ ...productForm, features: e.target.value })} 
              />
              <Button type="submit" className="w-full bg-[#003456] hover:bg-[#001d30]" data-testid="create-product-submit">
                Create Product
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Product</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Category</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Price (CAD)</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Status</th>
              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map(product => (
              <React.Fragment key={product.id}>
                {editingProduct === product.id ? (
                  // Edit Mode Row
                  <tr className="border-t border-gray-100 bg-blue-50">
                    <td colSpan={5} className="px-6 py-6">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-lg text-[#0a0203]">Edit Product</h3>
                          <Button variant="ghost" size="sm" onClick={handleCancelEdit}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                            <Input 
                              value={editForm.name}
                              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                              required
                              data-testid="edit-product-name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Price (CAD) *</label>
                            <Input 
                              type="number"
                              step="0.01"
                              value={editForm.price}
                              onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                              required
                              data-testid="edit-product-price"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                          <textarea 
                            value={editForm.description}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            rows={2}
                            className="w-full px-3 py-2 border rounded-lg resize-none"
                            data-testid="edit-product-description"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                          <Input 
                            value={editForm.image_url}
                            onChange={(e) => setEditForm({ ...editForm, image_url: e.target.value })}
                            data-testid="edit-product-image"
                          />
                        </div>

                        <div className="grid md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                            <select
                              value={editForm.category}
                              onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                              className="w-full p-2 border rounded-md"
                              data-testid="edit-product-category"
                            >
                              <option value="freeze-dryer">Freeze Dryer</option>
                              <option value="accessory">Accessory</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Size</label>
                            <Input 
                              value={editForm.size}
                              onChange={(e) => setEditForm({ ...editForm, size: e.target.value })}
                              placeholder="e.g., Small, Medium, Large"
                              data-testid="edit-product-size"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Stock Status</label>
                            <select
                              value={editForm.in_stock ? 'true' : 'false'}
                              onChange={(e) => setEditForm({ ...editForm, in_stock: e.target.value === 'true' })}
                              className="w-full p-2 border rounded-md"
                              data-testid="edit-product-stock"
                            >
                              <option value="true">In Stock</option>
                              <option value="false">Out of Stock</option>
                            </select>
                          </div>
                        </div>

                        {/* Stock Quantity Management */}
                        <div className="grid md:grid-cols-2 gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Stock Quantity
                              <span className="text-gray-400 font-normal ml-1">(leave empty for unlimited)</span>
                            </label>
                            <Input 
                              type="number"
                              min="0"
                              value={editForm.stock_quantity}
                              onChange={(e) => setEditForm({ ...editForm, stock_quantity: e.target.value })}
                              placeholder="e.g., 50"
                              data-testid="edit-product-stock-quantity"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Low Stock Threshold
                              <span className="text-gray-400 font-normal ml-1">(alert when below)</span>
                            </label>
                            <Input 
                              type="number"
                              min="1"
                              value={editForm.low_stock_threshold}
                              onChange={(e) => setEditForm({ ...editForm, low_stock_threshold: e.target.value })}
                              placeholder="5"
                              data-testid="edit-product-low-stock-threshold"
                            />
                          </div>
                        </div>

                        {/* Features List */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Features</label>
                          <div className="space-y-2">
                            {(editForm.features || []).map((feature, index) => (
                              <div key={index} className="flex items-center gap-2">
                                <Input 
                                  value={feature}
                                  onChange={(e) => updateFeature(index, e.target.value)}
                                  placeholder="Enter feature"
                                  className="flex-1"
                                  data-testid={`edit-product-feature-${index}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => removeFeature(index)}
                                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                  data-testid={`remove-feature-${index}`}
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={addFeature}
                              className="flex items-center gap-2 text-[#003456] hover:text-[#001d30] text-sm font-medium mt-2"
                              data-testid="add-feature-btn"
                            >
                              <Plus className="h-4 w-4" /> Add Feature
                            </button>
                          </div>
                        </div>

                        {/* Image Upload */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Product Image</label>
                          <div className="flex items-start gap-4">
                            {editForm.image_url && (
                              <img 
                                src={editForm.image_url} 
                                alt="Product preview" 
                                className="w-24 h-24 rounded-lg object-cover border"
                              />
                            )}
                            <div className="flex-1 space-y-2">
                              <Input 
                                value={editForm.image_url}
                                onChange={(e) => setEditForm({ ...editForm, image_url: e.target.value })}
                                placeholder="Image URL or upload below"
                                data-testid="edit-product-image"
                              />
                              <div className="flex items-center gap-2">
                                <input
                                  ref={fileInputRef}
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp"
                                  onChange={(e) => handleImageUpload(e.target.files[0])}
                                  className="hidden"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => fileInputRef.current?.click()}
                                  disabled={uploading}
                                  className="text-sm"
                                  data-testid="upload-image-btn"
                                >
                                  {uploading ? (
                                    'Uploading...'
                                  ) : (
                                    <>
                                      <Upload className="h-4 w-4 mr-2" /> Upload Image
                                    </>
                                  )}
                                </Button>
                                <span className="text-xs text-gray-500">JPG, PNG, WebP</span>
                              </div>
                            </div>
                          </div>
                          {/* Main Image Description */}
                          {editForm.image_url && (
                            <div className="mt-3">
                              <label className="block text-xs font-medium text-gray-500 mb-1">
                                Main Image Description
                              </label>
                              <Input
                                value={editForm.image_description || ''}
                                onChange={(e) => setEditForm({ ...editForm, image_description: e.target.value })}
                                placeholder="e.g., HomeLyph Pro 500 - Front view with stainless steel finish"
                                className="text-sm"
                                data-testid="main-image-description"
                              />
                              <p className="text-xs text-gray-400 mt-1">Displays below the main product image</p>
                            </div>
                          )}
                        </div>

                        {/* Multiple Images Gallery (Carousel Images) */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Additional Images (Carousel)
                            <span className="text-gray-400 font-normal ml-1">- These will show in the product carousel with descriptions</span>
                          </label>
                          <div className="space-y-3">
                            {/* Thumbnail Gallery with Descriptions */}
                            {(editForm.images || []).length > 0 && (
                              <div className="space-y-3">
                                {(editForm.images || []).map((img, idx) => {
                                  // Support both string URLs and {url, description} objects
                                  const imgUrl = typeof img === 'string' ? img : img.url;
                                  const imgDesc = typeof img === 'string' ? '' : (img.description || '');
                                  
                                  return (
                                    <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border">
                                      <div className="relative group flex-shrink-0">
                                        <img 
                                          src={imgUrl} 
                                          alt={imgDesc || `Gallery ${idx + 1}`} 
                                          className="w-24 h-24 rounded-lg object-cover border"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => removeImage(idx)}
                                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                          data-testid={`remove-gallery-image-${idx}`}
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      </div>
                                      <div className="flex-1">
                                        <label className="block text-xs font-medium text-gray-500 mb-1">
                                          Image {idx + 1} Description
                                        </label>
                                        <Input
                                          value={imgDesc}
                                          onChange={(e) => {
                                            const newImages = [...(editForm.images || [])];
                                            newImages[idx] = { url: imgUrl, description: e.target.value };
                                            setEditForm({ ...editForm, images: newImages });
                                          }}
                                          placeholder="e.g., Front view showing control panel"
                                          className="text-sm"
                                          data-testid={`image-description-${idx}`}
                                        />
                                        <p className="text-xs text-gray-400 mt-1">This will display below the image on the product page</p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {/* Upload Multiple Images */}
                            <div className="flex items-center gap-2">
                              <input
                                ref={multiFileInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                multiple
                                onChange={(e) => handleMultipleImageUpload(e.target.files)}
                                className="hidden"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => multiFileInputRef.current?.click()}
                                disabled={uploading}
                                className="text-sm"
                                data-testid="upload-gallery-images-btn"
                              >
                                {uploading ? (
                                  'Uploading...'
                                ) : (
                                  <>
                                    <Image className="h-4 w-4 mr-2" /> Add Gallery Images
                                  </>
                                )}
                              </Button>
                              <span className="text-xs text-gray-500">Select multiple images</span>
                            </div>
                          </div>
                        </div>

                        {/* Featured Toggle */}
                        <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                          <input
                            type="checkbox"
                            id={`featured-${product.id}`}
                            checked={editForm.featured || false}
                            onChange={(e) => setEditForm({ ...editForm, featured: e.target.checked })}
                            className="w-5 h-5 text-amber-500 rounded border-gray-300 focus:ring-amber-500"
                            data-testid="edit-product-featured"
                          />
                          <label htmlFor={`featured-${product.id}`} className="flex items-center gap-2 cursor-pointer">
                            <Star className="h-5 w-5 text-amber-500" />
                            <div>
                              <span className="font-medium text-gray-700">Featured Product</span>
                              <p className="text-xs text-gray-500">Show this product in featured sections across the website</p>
                            </div>
                          </label>
                        </div>

                        {/* Sale Settings */}
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-4">
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              id={`on-sale-${product.id}`}
                              checked={editForm.on_sale || false}
                              onChange={(e) => setEditForm({ ...editForm, on_sale: e.target.checked })}
                              className="w-5 h-5 text-green-500 rounded border-gray-300 focus:ring-green-500"
                              data-testid="edit-product-on-sale"
                            />
                            <label htmlFor={`on-sale-${product.id}`} className="flex items-center gap-2 cursor-pointer">
                              <Tag className="h-5 w-5 text-green-500" />
                              <div>
                                <span className="font-medium text-gray-700">On Sale</span>
                                <p className="text-xs text-gray-500">Enable sale pricing for this product</p>
                              </div>
                            </label>
                          </div>
                          
                          {editForm.on_sale && (
                            <div className="space-y-4 pl-8 border-l-2 border-green-200">
                              <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Sale Type</label>
                                  <select
                                    value={editForm.sale_type || 'fixed'}
                                    onChange={(e) => setEditForm({ ...editForm, sale_type: e.target.value })}
                                    className="w-full p-2 border rounded-md"
                                    data-testid="edit-sale-type"
                                  >
                                    <option value="fixed">Fixed Price (Same for all currencies)</option>
                                    <option value="percentage">Percentage Off (%)</option>
                                    <option value="regional">Charm Pricing (Set price per currency)</option>
                                  </select>
                                </div>
                                {editForm.sale_type !== 'regional' && (
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      {editForm.sale_type === 'percentage' ? 'Discount %' : 'Sale Price (Base)'}
                                    </label>
                                    <div className="relative">
                                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                        {editForm.sale_type === 'percentage' ? <Percent className="h-4 w-4" /> : <DollarSign className="h-4 w-4" />}
                                      </span>
                                      <Input 
                                        type="number"
                                        min="0"
                                        value={editForm.sale_value || ''}
                                        onChange={(e) => setEditForm({ ...editForm, sale_value: e.target.value })}
                                        className="pl-10"
                                        placeholder={editForm.sale_type === 'percentage' ? '20' : '1999'}
                                        data-testid="edit-sale-value"
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              {/* Charm Pricing - Per Currency Sale Prices */}
                              {editForm.sale_type === 'regional' && (
                                <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                                  <div className="flex items-center gap-2 mb-3">
                                    <DollarSign className="h-5 w-5 text-amber-600" />
                                    <span className="font-medium text-gray-700">Charm Pricing by Currency</span>
                                  </div>
                                  <p className="text-xs text-gray-500 mb-3">Set psychologically appealing prices ending in .99 or .00 for each currency</p>
                                  <div className="grid md:grid-cols-3 gap-4">
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">USD Sale Price</label>
                                      <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs">$</span>
                                        <Input 
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={editForm.regional_sale_prices?.USD || ''}
                                          onChange={(e) => setEditForm({ 
                                            ...editForm, 
                                            regional_sale_prices: { 
                                              ...editForm.regional_sale_prices, 
                                              USD: e.target.value ? parseFloat(e.target.value) : null 
                                            } 
                                          })}
                                          className="pl-8"
                                          placeholder="3999.00"
                                          data-testid="charm-price-usd"
                                        />
                                      </div>
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">CAD Sale Price</label>
                                      <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs">$</span>
                                        <Input 
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={editForm.regional_sale_prices?.CAD || ''}
                                          onChange={(e) => setEditForm({ 
                                            ...editForm, 
                                            regional_sale_prices: { 
                                              ...editForm.regional_sale_prices, 
                                              CAD: e.target.value ? parseFloat(e.target.value) : null 
                                            } 
                                          })}
                                          className="pl-8"
                                          placeholder="4799.00"
                                          data-testid="charm-price-cad"
                                        />
                                      </div>
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">EUR Sale Price</label>
                                      <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs">€</span>
                                        <Input 
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={editForm.regional_sale_prices?.EUR || ''}
                                          onChange={(e) => setEditForm({ 
                                            ...editForm, 
                                            regional_sale_prices: { 
                                              ...editForm.regional_sale_prices, 
                                              EUR: e.target.value ? parseFloat(e.target.value) : null 
                                            } 
                                          })}
                                          className="pl-8"
                                          placeholder="3699.00"
                                          data-testid="charm-price-eur"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <p className="text-xs text-amber-700 mt-3">
                                    <strong>Tip:</strong> Prices ending in .99 or .00 (e.g., $4,799.00) perform better than calculated discounts (e.g., $4,841.10)
                                  </p>
                                </div>
                              )}
                              
                              <div className="grid md:grid-cols-2 gap-4 mt-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <Calendar className="h-4 w-4 inline mr-1" />
                                    Start Date (Optional)
                                  </label>
                                  <Input 
                                    type="datetime-local"
                                    value={editForm.sale_start_date || ''}
                                    onChange={(e) => setEditForm({ ...editForm, sale_start_date: e.target.value })}
                                    data-testid="edit-sale-start"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    <Calendar className="h-4 w-4 inline mr-1" />
                                    End Date (Optional)
                                  </label>
                                  <Input 
                                    type="datetime-local"
                                    value={editForm.sale_end_date || ''}
                                    onChange={(e) => setEditForm({ ...editForm, sale_end_date: e.target.value })}
                                    data-testid="edit-sale-end"
                                  />
                                </div>
                              </div>
                              <p className="text-xs text-amber-600 mt-2 bg-amber-50 p-2 rounded">
                                <strong>Note:</strong> Leave dates empty for sale to be always active. If you set a start date in the future, the sale won't show until that date.
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Region-Specific Pricing */}
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Globe className="h-5 w-5 text-blue-500" />
                            <span className="font-medium text-gray-700">Region-Specific Pricing</span>
                          </div>
                          <p className="text-xs text-gray-500 mb-3">Set different base prices for each region. Leave empty to use default price.</p>
                          <div className="grid md:grid-cols-3 gap-4">
                            {REGIONS.map(region => (
                              <div key={region.code}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{region.name}</label>
                                <div className="relative">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                                  <Input 
                                    type="number"
                                    min="0"
                                    value={editForm.region_prices?.[region.code] || ''}
                                    onChange={(e) => updateRegionPrice(region.code, e.target.value)}
                                    className="pl-8"
                                    placeholder={editForm.price || '0'}
                                    data-testid={`region-price-${region.code}`}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* PDF Downloads */}
                        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-4">
                          <div className="flex items-center gap-2 mb-2">
                            <FileText className="h-5 w-5 text-purple-500" />
                            <span className="font-medium text-gray-700">Product Downloads</span>
                          </div>
                          <p className="text-xs text-gray-500 mb-3">Upload PDF flyers and spec sheets for customers to download</p>
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Product Flyer (PDF)</label>
                              <div className="space-y-2">
                                {editForm.flyer_pdf_url && (
                                  <div className="flex items-center gap-2 text-sm text-purple-600">
                                    <FileText className="h-4 w-4" />
                                    <a href={editForm.flyer_pdf_url} target="_blank" rel="noopener noreferrer" className="underline truncate max-w-[200px]">
                                      View Current Flyer
                                    </a>
                                    <button 
                                      type="button" 
                                      onClick={() => setEditForm({ ...editForm, flyer_pdf_url: '' })}
                                      className="text-red-500 hover:text-red-700"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </div>
                                )}
                                <input
                                  ref={pdfInputRef}
                                  type="file"
                                  accept="application/pdf"
                                  onChange={(e) => handlePdfUpload(e.target.files[0], 'flyer_pdf_url')}
                                  className="hidden"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => pdfInputRef.current?.click()}
                                  disabled={uploadingPdf}
                                  className="text-sm w-full"
                                  data-testid="upload-flyer-btn"
                                >
                                  {uploadingPdf ? 'Uploading...' : (
                                    <>
                                      <Upload className="h-4 w-4 mr-2" /> Upload Flyer PDF
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Spec Sheet (PDF)</label>
                              <div className="space-y-2">
                                {editForm.spec_sheet_url && (
                                  <div className="flex items-center gap-2 text-sm text-purple-600">
                                    <FileText className="h-4 w-4" />
                                    <a href={editForm.spec_sheet_url} target="_blank" rel="noopener noreferrer" className="underline truncate max-w-[200px]">
                                      View Current Spec Sheet
                                    </a>
                                    <button 
                                      type="button" 
                                      onClick={() => setEditForm({ ...editForm, spec_sheet_url: '' })}
                                      className="text-red-500 hover:text-red-700"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </div>
                                )}
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => {
                                    const input = document.createElement('input');
                                    input.type = 'file';
                                    input.accept = 'application/pdf';
                                    input.onchange = (e) => handlePdfUpload(e.target.files[0], 'spec_sheet_url');
                                    input.click();
                                  }}
                                  disabled={uploadingPdf}
                                  className="text-sm w-full"
                                  data-testid="upload-spec-sheet-btn"
                                >
                                  {uploadingPdf ? 'Uploading...' : (
                                    <>
                                      <Upload className="h-4 w-4 mr-2" /> Upload Spec Sheet PDF
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Specifications Editor */}
                        <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Settings className="h-5 w-5 text-slate-500" />
                            <span className="font-medium text-gray-700">Product Specifications</span>
                          </div>
                          <div className="space-y-2">
                            {Object.entries(editForm.specifications || {}).map(([key, value]) => (
                              <div key={key} className="flex items-center gap-2 bg-white p-2 rounded border">
                                <span className="font-medium text-gray-700 min-w-[120px]">{key}:</span>
                                <span className="text-gray-600 flex-1">{value}</span>
                                <button
                                  type="button"
                                  onClick={() => removeSpecification(key)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <Input
                              placeholder="Spec name (e.g., Capacity)"
                              value={newSpecKey}
                              onChange={(e) => setNewSpecKey(e.target.value)}
                              className="w-1/3"
                            />
                            <Input
                              placeholder="Value (e.g., 10 lbs)"
                              value={newSpecValue}
                              onChange={(e) => setNewSpecValue(e.target.value)}
                              className="flex-1"
                            />
                            <Button type="button" onClick={addSpecification} variant="outline" size="sm">
                              <Plus className="h-4 w-4 mr-1" /> Add
                            </Button>
                          </div>
                        </div>

                        {/* Product Status (Active / Coming Soon / Pre-Order) */}
                        <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200 space-y-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Clock className="h-5 w-5 text-indigo-500" />
                            <span className="font-medium text-gray-700">Product Availability Status</span>
                          </div>
                          <div className="space-y-3">
                            {PRODUCT_STATUSES.map(status => (
                              <label 
                                key={status.value}
                                className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                  editForm.status === status.value 
                                    ? `border-${status.color}-500 bg-${status.color}-50` 
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="productStatus"
                                  value={status.value}
                                  checked={editForm.status === status.value}
                                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                                  className="sr-only"
                                />
                                <div className={`w-4 h-4 rounded-full border-2 ${
                                  editForm.status === status.value 
                                    ? `border-${status.color}-500 bg-${status.color}-500` 
                                    : 'border-gray-300'
                                }`}>
                                  {editForm.status === status.value && (
                                    <div className="w-full h-full rounded-full flex items-center justify-center">
                                      <div className="w-1.5 h-1.5 rounded-full bg-white" />
                                    </div>
                                  )}
                                </div>
                                <span className="font-medium">{status.label}</span>
                              </label>
                            ))}
                          </div>
                          
                          {editForm.status === 'pre_order' && (
                            <div className="grid md:grid-cols-2 gap-4 pt-3 border-t border-indigo-200">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Deposit Amount ($)</label>
                                <Input
                                  type="number"
                                  min="0"
                                  value={editForm.preorder_deposit || ''}
                                  onChange={(e) => setEditForm({ ...editForm, preorder_deposit: e.target.value })}
                                  placeholder="500"
                                />
                                <p className="text-xs text-gray-500 mt-1">Leave empty for full payment</p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Est. Availability Date</label>
                                <Input
                                  type="date"
                                  value={editForm.estimated_availability || ''}
                                  onChange={(e) => setEditForm({ ...editForm, estimated_availability: e.target.value })}
                                />
                              </div>
                            </div>
                          )}
                          
                          {editForm.status === 'coming_soon' && (
                            <div className="pt-3 border-t border-indigo-200">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Est. Availability Date</label>
                                <Input
                                  type="date"
                                  value={editForm.estimated_availability || ''}
                                  onChange={(e) => setEditForm({ ...editForm, estimated_availability: e.target.value })}
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Product Add-ons */}
                        <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200 space-y-4">
                          <div className="flex items-center gap-2 mb-2">
                            <ShoppingBag className="h-5 w-5 text-cyan-500" />
                            <span className="font-medium text-gray-700">Product Add-ons & Upgrades</span>
                          </div>
                          <p className="text-xs text-gray-500">Add optional upgrades or accessories that customers can add to this product</p>
                          
                          {/* Existing Add-ons */}
                          <div className="space-y-2">
                            {(editForm.addons || []).map(addon => (
                              <div key={addon.id} className="flex items-center gap-3 bg-white p-3 rounded border">
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  addon.type === 'radio' ? 'bg-amber-100 text-amber-700' : 'bg-cyan-100 text-cyan-700'
                                }`}>
                                  {addon.type === 'radio' ? 'Upgrade' : 'Add-on'}
                                </span>
                                <div className="flex-1">
                                  <span className="font-medium">{addon.name}</span>
                                  {addon.description && <p className="text-xs text-gray-500">{addon.description}</p>}
                                </div>
                                <div className="text-right">
                                  <span className="font-bold text-green-600">+${addon.price} USD</span>
                                  {addon.price_cad && (
                                    <span className="text-xs text-gray-500 ml-2">/ ${addon.price_cad} CAD</span>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeAddon(addon.id)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                          
                          {/* Add New Add-on */}
                          <div className="space-y-2 pt-2 border-t border-cyan-200">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                              <Input
                                placeholder="Name"
                                value={newAddon.name}
                                onChange={(e) => setNewAddon({ ...newAddon, name: e.target.value })}
                              />
                              <Input
                                type="number"
                                placeholder="Price USD"
                                value={newAddon.price}
                                onChange={(e) => setNewAddon({ ...newAddon, price: e.target.value })}
                              />
                              <Input
                                type="number"
                                placeholder="Price CAD"
                                value={newAddon.price_cad || ''}
                                onChange={(e) => setNewAddon({ ...newAddon, price_cad: e.target.value })}
                              />
                              <select
                                className="border rounded-md px-3 py-2"
                                value={newAddon.type}
                                onChange={(e) => setNewAddon({ ...newAddon, type: e.target.value })}
                              >
                                <option value="checkbox">Add-on (Multiple)</option>
                                <option value="radio">Upgrade (Single)</option>
                              </select>
                            </div>
                            <div className="flex gap-2">
                              <Input
                                placeholder="Description (optional)"
                                value={newAddon.description}
                                onChange={(e) => setNewAddon({ ...newAddon, description: e.target.value })}
                                className="flex-1"
                              />
                              <Button type="button" onClick={addAddon} variant="outline">
                                <Plus className="h-4 w-4 mr-1" /> Add
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Ships With (What's Included) */}
                        <div className="p-4 bg-teal-50 rounded-lg border border-teal-200 space-y-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Package className="h-5 w-5 text-teal-500" />
                            <span className="font-medium text-gray-700">What's Included (Ships With)</span>
                          </div>
                          <p className="text-xs text-gray-500">List items that are included when this product ships (e.g., trays, pump, accessories)</p>
                          
                          {/* Existing Ships With Items */}
                          <div className="space-y-2">
                            {(editForm.ships_with || []).map((item, index) => (
                              <div key={index} className="flex items-center gap-2">
                                <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-white text-xs font-bold">{index + 1}</span>
                                </div>
                                <Input
                                  value={item}
                                  onChange={(e) => updateShipsWith(index, e.target.value)}
                                  placeholder="Item name (e.g., Stainless Steel Trays)"
                                  className="flex-1"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeShipsWith(index)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                          
                          {/* Add New Item Button */}
                          <Button 
                            type="button" 
                            onClick={addShipsWith} 
                            variant="outline"
                            className="w-full border-teal-300 text-teal-700 hover:bg-teal-100"
                          >
                            <Plus className="h-4 w-4 mr-1" /> Add Item
                          </Button>
                          
                          {/* Quick Add Common Items */}
                          <div className="pt-2 border-t border-teal-200">
                            <p className="text-xs text-gray-500 mb-2">Quick add common items:</p>
                            <div className="flex flex-wrap gap-2">
                              {['Freeze Dryer Unit', 'Stainless Steel Trays', 'Vacuum Pump', 'Pump Oil', 'Mylar Bags Starter Kit', 'Oxygen Absorbers', 'Impulse Sealer', 'User Manual'].map(item => (
                                <button
                                  key={item}
                                  type="button"
                                  onClick={() => {
                                    if (!(editForm.ships_with || []).includes(item)) {
                                      setEditForm({ ...editForm, ships_with: [...(editForm.ships_with || []), item] });
                                    }
                                  }}
                                  disabled={(editForm.ships_with || []).includes(item)}
                                  className={`text-xs px-2 py-1 rounded border ${
                                    (editForm.ships_with || []).includes(item)
                                      ? 'bg-teal-100 text-teal-400 border-teal-200 cursor-not-allowed'
                                      : 'bg-white text-teal-700 border-teal-300 hover:bg-teal-50'
                                  }`}
                                >
                                  + {item}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Badge Configuration */}
                        <div className="p-4 bg-rose-50 rounded-lg border border-rose-200 space-y-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Tag className="h-5 w-5 text-rose-500" />
                            <span className="font-medium text-gray-700">Product Badges</span>
                          </div>
                          <p className="text-xs text-gray-500">Customize the badges shown on this product (leave empty to use global defaults)</p>
                          <div className="grid md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                <Truck className="h-4 w-4" /> Shipping Badge
                              </label>
                              <Input
                                value={editForm.badges?.shipping || ''}
                                onChange={(e) => updateBadge('shipping', e.target.value)}
                                placeholder="Fast Shipping"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                <Shield className="h-4 w-4" /> Warranty Badge
                              </label>
                              <Input
                                value={editForm.badges?.warranty || ''}
                                onChange={(e) => updateBadge('warranty', e.target.value)}
                                placeholder="5-Year Warranty"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                                <RotateCcw className="h-4 w-4" /> Returns Badge
                              </label>
                              <Input
                                value={editForm.badges?.returns || ''}
                                onChange={(e) => updateBadge('returns', e.target.value)}
                                placeholder="30-Day Returns"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Warranty Info */}
                        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200 space-y-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Shield className="h-5 w-5 text-emerald-500" />
                            <span className="font-medium text-gray-700">Warranty Information</span>
                          </div>
                          <textarea
                            className="w-full border rounded-lg px-3 py-2"
                            rows={3}
                            value={editForm.warranty_info || ''}
                            onChange={(e) => setEditForm({ ...editForm, warranty_info: e.target.value })}
                            placeholder="Describe the warranty coverage for this product. This will link to the full warranty page."
                          />
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t">
                          <Button variant="outline" onClick={handleCancelEdit}>
                            Cancel
                          </Button>
                          <Button 
                            onClick={() => handleSaveEdit(product.id)}
                            disabled={saving}
                            className="bg-[#003456] hover:bg-[#001d30]"
                            data-testid="save-product-edit"
                          >
                            {saving ? 'Saving...' : (
                              <>
                                <Save className="h-4 w-4 mr-2" /> Save Changes
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  // Normal Display Row
                  <tr className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        {product.image_url ? (
                          <img src={product.image_url} alt={product.name} className="w-12 h-12 rounded-lg object-cover" />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Package className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-[#0a0203]">{product.name}</span>
                            {product.featured && (
                              <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                            )}
                          </div>
                          {product.size && (
                            <span className="block text-xs text-gray-500">{product.size}</span>
                          )}
                          {product.images && product.images.length > 0 && (
                            <span className="text-xs text-blue-500">{product.images.length + 1} images</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600 capitalize">{product.category?.replace('-', ' ')}</td>
                    <td className="px-6 py-4 font-medium" style={{ fontFamily: 'JetBrains Mono' }}>
                      ${product.price?.toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        {/* Main stock status */}
                        <span className={`px-3 py-1 rounded-full text-xs font-medium inline-block w-fit ${
                          product.in_stock !== false 
                            ? (product.stock_quantity !== null && product.stock_quantity !== undefined && product.stock_quantity <= (product.low_stock_threshold || 5) && product.stock_quantity > 0)
                              ? 'bg-amber-100 text-amber-600'
                              : 'bg-green-100 text-green-600'
                            : 'bg-red-100 text-red-600'
                        }`}>
                          {product.in_stock !== false 
                            ? (product.stock_quantity !== null && product.stock_quantity !== undefined && product.stock_quantity <= (product.low_stock_threshold || 5) && product.stock_quantity > 0)
                              ? 'Low Stock'
                              : 'In Stock'
                            : 'Out of Stock'
                          }
                        </span>
                        {/* Inline stock quantity edit */}
                        {inlineStockEdit === product.id ? (
                          <div className="flex items-center gap-1 mt-1">
                            <Input
                              type="number"
                              min="0"
                              value={inlineStockValue}
                              onChange={(e) => setInlineStockValue(e.target.value)}
                              placeholder="Qty"
                              className="w-20 h-7 text-xs"
                              data-testid={`inline-stock-input-${product.id}`}
                              autoFocus
                            />
                            <button
                              onClick={() => handleInlineStockSave(product.id)}
                              className="p-1 text-green-600 hover:bg-green-50 rounded"
                              data-testid={`inline-stock-save-${product.id}`}
                            >
                              <Save className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => setInlineStockEdit(null)}
                              className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setInlineStockEdit(product.id);
                              setInlineStockValue(product.stock_quantity ?? '');
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline text-left"
                            data-testid={`inline-stock-edit-${product.id}`}
                          >
                            {product.stock_quantity !== null && product.stock_quantity !== undefined 
                              ? `${product.stock_quantity} units` 
                              : 'Set stock →'
                            }
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button 
                          onClick={() => handleEditClick(product)} 
                          className="text-blue-500 hover:text-blue-700 p-1"
                          title="Edit product"
                          data-testid={`edit-product-${product.id}`}
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                        <button 
                          onClick={() => handleDeleteProduct(product.id)} 
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Delete product"
                          data-testid={`delete-product-${product.id}`}
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {products.length === 0 && (
          <div className="text-center py-12">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No products yet</p>
            <p className="text-sm text-gray-400 mt-1">Add your first product to get started</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsTab;
